import {usePlayers} from '@glark-newco/game-library';
import {Button, Paper, Select, Stack} from '@mantine/core';
import {IPointData} from 'pixi.js';
import {useCallback, useMemo} from 'react';
import {useIsPopupEnabled, useNodePopupLocation, useSetNodePopupLocation} from '../state/networkEditStore';
import {useDeleteNode, usePlacedPlayerIds, usePlayerByNodeId, useSetNodeOwner} from '../state/networkHooks';
import {NodeId} from '../state/networkStore';


type AssignNodePopupProps = {
    nodeId: NodeId,
} & IPointData;

export function AssignNodePopup({x, y, nodeId}: AssignNodePopupProps) {
    const setOwnership = useSetNodeOwner();
    const deleteNode = useDeleteNode();
    const {players} = usePlayers();
    const playerPopup = useNodePopupLocation();
    const setPlayerPopup = useSetNodePopupLocation();
    const isPopupEnabled = useIsPopupEnabled();
    const placedPlayerIds = usePlacedPlayerIds();
    const existingPlayerId = usePlayerByNodeId(nodeId);

    const updatePositionInEdit = useCallback((nodeID: NodeId | undefined) => {
        setPlayerPopup(existing => ({pixi: existing.pixi, mantine: nodeID}));
    }, [setPlayerPopup]);

    const assignPlayerToNode = useCallback((selectedPlayerId: string | null) => {
        if (!nodeId) throw new Error('nodeId not defined');

        setOwnership(nodeId, (selectedPlayerId && selectedPlayerId !== 'None') ? selectedPlayerId : undefined);
        setPlayerPopup(() => ({pixi: undefined, mantine: undefined})); // close popup
    }, [nodeId, setPlayerPopup]);

    const handleRemoveNode = useCallback(() => {
        if (!nodeId) throw new Error('nodeId not defined');
        deleteNode(nodeId);
        setPlayerPopup(() => ({pixi: undefined, mantine: undefined})); // close popup
    }, [nodeId, deleteNode, setPlayerPopup]);

    const groupedSelectValues = useMemo(() => {
        const playerOptions = players.map(p => ({value: p.playerId, label: p.name}));
        return [{
            group: 'Remove Player',
            items: [{value: 'None', label: 'No player assigned'}],
        }, {
            group: 'Assign',
            items: playerOptions.filter(o => !placedPlayerIds.includes(o.value)),
        }, {
            group: 'Reassign',
            items: playerOptions.filter(o => placedPlayerIds.includes(o.value)),
        }];
    }, [players, placedPlayerIds]);
  
    return (
        <>
            {(nodeId === playerPopup.mantine || nodeId === playerPopup.pixi) && isPopupEnabled &&
                <Paper
                    data-testid={`Player-select-${nodeId}`}
                    p={'md'}
                    style={{
                        position: 'absolute',
                        backgroundColor: 'beige',
                        pointerEvents: 'all',
                        width: '12%',
                        top: `${(y / 10.8) + 6}%`,
                        left: `${x / 19.2}%`,
                        transform: 'translate(-50%, -50%)',
                    }}
                    onPointerEnter={() => updatePositionInEdit(nodeId)}
                    onPointerLeave={() => updatePositionInEdit(undefined)}
                >
                    <Stack>
                        <Select
                            aria-label={'Assign player'}
                            wrapperProps={{'data-testid': 'design_player_select'}}
                            defaultValue={'None'}
                            comboboxProps={{
                                position: 'bottom',
                                offset: 0,
                            }}
                            onChange={assignPlayerToNode}
                            value={existingPlayerId}
                            data={groupedSelectValues}
                        />
                        <Button data-testid={'design_player_remove'} variant={'light'} color={'red'} onClick={handleRemoveNode}>Remove Position</Button>
                    </Stack>
                </Paper>
            }
        </>
    );
}
